import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import {
  FiTrendingUp, FiLoader, FiDollarSign, FiPackage,
  FiBarChart2, FiActivity
} from "react-icons/fi";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .pv-root {
    font-family: 'Montserrat', sans-serif;
    padding: 24px;
    max-width: 80rem;
    margin: 0 auto;
    min-height: 100vh;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  @media (max-width: 640px) {
    .pv-root {
      padding: 16px;
    }
  }

  /* Header */
  .pv-header {
    margin-bottom: 32px;
  }

  .pv-title {
    font-size: 28px;
    font-weight: 700;
    background: linear-gradient(135deg, #1e293b, #475569);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 8px;
  }

  @media (min-width: 640px) {
    .pv-title {
      font-size: 30px;
    }
  }

  .pv-subtitle {
    color: #6b7280;
  }

  /* Summary Cards */
  .pv-summary-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }

  @media (min-width: 768px) {
    .pv-summary-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .pv-summary-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    padding: 24px;
    border-left: 4px solid;
  }

  .pv-summary-card-blue {
    border-left-color: #3b82f6;
  }

  .pv-summary-card-green {
    border-left-color: #22c55e;
  }

  .pv-summary-card-purple {
    border-left-color: #8b5cf6;
  }

  .pv-summary-card-orange {
    border-left-color: #f59e0b;
  }

  .pv-summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .pv-summary-label {
    font-size: 14px;
    color: #6b7280;
  }

  .pv-summary-icon-blue { color: #3b82f6; }
  .pv-summary-icon-green { color: #22c55e; }
  .pv-summary-icon-purple { color: #8b5cf6; }
  .pv-summary-icon-orange { color: #f59e0b; }

  .pv-summary-value {
    font-size: 28px;
    font-weight: 700;
    color: #1f2937;
  }

  .pv-summary-value-capitalize {
    font-size: 28px;
    font-weight: 700;
    color: #1f2937;
    text-transform: capitalize;
  }

  /* Additional Info */
  .pv-info-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }

  @media (min-width: 768px) {
    .pv-info-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  .pv-info-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    padding: 24px;
  }

  .pv-info-label {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  .pv-info-value {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
  }

  /* Monthly History */
  .pv-history-section {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    margin-bottom: 32px;
  }

  .pv-section-header {
    padding: 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  .pv-section-title {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pv-section-body {
    padding: 24px;
  }

  .pv-empty {
    text-align: center;
    padding: 32px 0;
    color: #6b7280;
  }

  .pv-history-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .pv-history-item {
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    cursor: pointer;
    transition: all 0.2s;
    background: #f9fafb;
  }

  .pv-history-item:hover {
    background: #f3f4f6;
  }

  .pv-history-item-selected {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  .pv-history-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .pv-history-month {
    font-weight: 500;
    color: #1f2937;
  }

  .pv-history-stats {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .pv-history-stat {
    font-size: 14px;
  }

  .pv-history-stat-label {
    color: #6b7280;
  }

  .pv-history-stat-value {
    font-weight: 500;
    color: #1f2937;
  }

  .pv-history-expanded {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
  }

  .pv-history-expanded-label {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 8px;
  }

  .pv-history-purchases {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .pv-purchase-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    background: #ffffff;
    border-radius: 8px;
    padding: 8px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .pv-purchase-name {
    color: #1f2937;
  }

  .pv-purchase-details {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .pv-purchase-detail {
    color: #6b7280;
  }

  /* Recent Purchases Table */
  .pv-recent-section {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .pv-table-wrap {
    overflow-x: auto;
  }

  .pv-table {
    width: 100%;
    border-collapse: collapse;
  }

  .pv-table-head {
    text-align: left;
    font-size: 14px;
    color: #6b7280;
    border-bottom: 1px solid #e5e7eb;
  }

  .pv-table-head th {
    padding-bottom: 12px;
    font-weight: 500;
  }

  .pv-table-body tr {
    border-bottom: 1px solid #e5e7eb;
  }

  .pv-table-body td {
    padding: 12px 0;
    font-size: 14px;
  }

  .pv-table-body .pv-product-name {
    color: #1f2937;
  }

  .pv-table-body .pv-cell {
    color: #6b7280;
  }

  /* Loading State */
  .pv-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  .pv-loading-content {
    text-align: center;
  }

  .pv-spinner {
    animation: pv-spin 1s linear infinite;
    font-size: 36px;
    color: #3b82f6;
    margin: 0 auto 16px;
    display: block;
  }

  @keyframes pv-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .pv-loading-text {
    color: #6b7280;
  }

  /* Error State */
  .pv-error {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f1f5f9 0%, #eff6ff 50%, #f1f5f9 100%);
  }

  .pv-error-content {
    text-align: center;
    color: #ef4444;
  }

  .pv-error-text {
    margin-bottom: 16px;
  }

  .pv-retry-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: #ffffff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    transition: background 0.2s;
  }

  .pv-retry-btn:hover {
    background: #2563eb;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .pv-history-top {
      flex-direction: column;
      align-items: flex-start;
    }

    .pv-history-stats {
      flex-direction: column;
      gap: 4px;
    }

    .pv-purchase-item {
      flex-direction: column;
      align-items: flex-start;
    }

    .pv-purchase-details {
      flex-direction: column;
      gap: 4px;
    }

    .pv-summary-value {
      font-size: 22px;
    }

    .pv-info-value {
      font-size: 20px;
    }

    .pv-table-body td {
      padding: 8px 0;
    }

    .pv-table-body .pv-cell {
      font-size: 12px;
    }
  }
`;

const PurchaseVolume = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    fetchPurchaseVolume();
  }, []);

  const fetchPurchaseVolume = async () => {
    try {
      setLoading(true);
      const { data: responseData } = await axios.get("/puap/purchase-volume");
      setData(responseData);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load purchase volume");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pv-loading">
        <style>{styles}</style>
        <div className="pv-loading-content">
          <FiLoader className="pv-spinner" />
          <p className="pv-loading-text">Loading purchase volume...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pv-error">
        <style>{styles}</style>
        <div className="pv-error-content">
          <p className="pv-error-text">{error}</p>
          <button onClick={fetchPurchaseVolume} className="pv-retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { summary, history, recentPurchases } = data;

  return (
    <div className="pv-root">
      <style>{styles}</style>

      <div className="pv-header">
        <h1 className="pv-title">Purchase Volume</h1>
        <p className="pv-subtitle">View your total PV and purchase history</p>
      </div>

      <div className="pv-summary-grid">
        <div className="pv-summary-card pv-summary-card-blue">
          <div className="pv-summary-header">
            <div className="pv-summary-label">Total PV</div>
            <FiTrendingUp className="pv-summary-icon-blue" />
          </div>
          <div className="pv-summary-value">{summary.totalPv}</div>
        </div>
        <div className="pv-summary-card pv-summary-card-green">
          <div className="pv-summary-header">
            <div className="pv-summary-label">Total Amount</div>
            <FiDollarSign className="pv-summary-icon-green" />
          </div>
          <div className="pv-summary-value">₹{summary.totalAmount?.toLocaleString()}</div>
        </div>
        <div className="pv-summary-card pv-summary-card-purple">
          <div className="pv-summary-header">
            <div className="pv-summary-label">Total Purchases</div>
            <FiPackage className="pv-summary-icon-purple" />
          </div>
          <div className="pv-summary-value">{summary.totalPurchases}</div>
        </div>
        <div className="pv-summary-card pv-summary-card-orange">
          <div className="pv-summary-header">
            <div className="pv-summary-label">Activity Status</div>
            <FiActivity className="pv-summary-icon-orange" />
          </div>
          <div className="pv-summary-value-capitalize">
            {summary.activityStatus}
          </div>
        </div>
      </div>

      <div className="pv-info-grid">
        <div className="pv-info-card">
          <div className="pv-info-label">Monthly Active Months</div>
          <div className="pv-info-value">{summary.monthlyActiveMonths}</div>
        </div>
        {summary.lastActivePurchaseDate && (
          <div className="pv-info-card">
            <div className="pv-info-label">Last Active Purchase</div>
            <div className="pv-info-value">
              {new Date(summary.lastActivePurchaseDate).toLocaleDateString()}
            </div>
          </div>
        )}
      </div>

      <div className="pv-history-section">
        <div className="pv-section-header">
          <h2 className="pv-section-title">
            <FiBarChart2 />
            Monthly History
          </h2>
        </div>
        <div className="pv-section-body">
          {history.length === 0 ? (
            <div className="pv-empty">No purchase history available</div>
          ) : (
            <div className="pv-history-list">
              {history.map((month) => (
                <div
                  key={month.month}
                  className={`pv-history-item ${
                    selectedMonth === month.month ? "pv-history-item-selected" : ""
                  }`}
                  onClick={() => setSelectedMonth(selectedMonth === month.month ? null : month.month)}
                >
                  <div className="pv-history-top">
                    <div className="pv-history-month">
                      {new Date(month.month + "-01").toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long"
                      })}
                    </div>
                    <div className="pv-history-stats">
                      <div className="pv-history-stat">
                        <span className="pv-history-stat-label">PV: </span>
                        <span className="pv-history-stat-value">{month.pv}</span>
                      </div>
                      <div className="pv-history-stat">
                        <span className="pv-history-stat-label">Amount: </span>
                        <span className="pv-history-stat-value">₹{month.amount?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  {selectedMonth === month.month && (
                    <div className="pv-history-expanded">
                      <div className="pv-history-expanded-label">Purchases this month:</div>
                      <div className="pv-history-purchases">
                        {month.purchases.map((purchase, idx) => (
                          <div key={idx} className="pv-purchase-item">
                            <span className="pv-purchase-name">{purchase.productName}</span>
                            <div className="pv-purchase-details">
                              <span className="pv-purchase-detail">Qty: {purchase.quantity}</span>
                              <span className="pv-purchase-detail">PV: {purchase.pv}</span>
                              <span className="pv-purchase-detail">₹{purchase.amount}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="pv-recent-section">
        <div className="pv-section-header">
          <h2 className="pv-section-title">
            <FiPackage />
            Recent Purchases
          </h2>
        </div>
        <div className="pv-section-body">
          {recentPurchases.length === 0 ? (
            <div className="pv-empty">No recent purchases</div>
          ) : (
            <div className="pv-table-wrap">
              <table className="pv-table">
                <thead className="pv-table-head">
                  <tr>
                    <th>Product</th>
                    <th>PV</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody className="pv-table-body">
                  {recentPurchases.map((purchase) => (
                    <tr key={purchase._id}>
                      <td className="pv-product-name">
                        {purchase.productId?.name || "Unknown Product"}
                      </td>
                      <td className="pv-cell">{purchase.pv}</td>
                      <td className="pv-cell">{purchase.quantity}</td>
                      <td className="pv-cell">
                        ₹{purchase.totalAmount?.toLocaleString()}
                      </td>
                      <td className="pv-cell">
                        {purchase.createdAt
                          ? new Date(purchase.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseVolume;